@file:Suppress("UNCHECKED_CAST_TO_EXTERNAL_INTERFACE")

package org.example.project.yard

import androidx.compose.runtime.*
import kotlinx.coroutines.launch
import org.example.project.service.ZustandManager
import org.example.project.enginetabular.CellColor
import org.example.project.enginetabular.TabularScreen
import org.example.project.service.YardApi
import org.example.project.service.YardLiveSync
import org.example.project.utils.All
import org.jetbrains.compose.web.css.Color

@Composable
fun WAAuskunftScreen() {
    val scope = rememberCoroutineScope()
    var eintraege by remember { mutableStateOf<List<All>>(emptyList()) }
    var lkwEintraege by remember { mutableStateOf<List<All>>(emptyList()) }
    var standorteByTour by remember { mutableStateOf<Map<String, List<All>>>(emptyMap()) }
    var palettenDefs by remember { mutableStateOf<List<All>>(emptyList()) }
    var kontrolleDefs by remember { mutableStateOf<List<All>>(emptyList()) }
    var selectedTournummer by remember { mutableStateOf<String?>(null) }
    var searchedKopf by remember { mutableStateOf<List<All>?>(emptyList()) }
    var searchedDetails by remember { mutableStateOf<List<All>?>(emptyList()) }
    val refreshVersion = YardLiveSync.version()

    LaunchedEffect(refreshVersion) {
        searchedKopf = emptyList()
        searchedDetails = emptyList()
        eintraege = YardApi.loadWaListe()
        lkwEintraege = YardApi.loadWaLkwListe()
        standorteByTour = eintraege
            .map { it.lieferscheinnummer }
            .filter { it.isNotBlank() }
            .distinct()
            .associateWith { YardApi.loadWaStandorteByTour(it) }
        palettenDefs = YardApi.loadPalettenDefinitionen().filter { it.aktivWa }
        kontrolleDefs = YardApi.loadKontrolleDefinitionen().filter { it.aktivWa }
    }

    val palHeaders = palettenDefs.map { it.bezeichnung }
    val headersTop = listOf(
        "lfd. Nr.", "Datum", "Angemeldet", "Zustand", "lieferscheinnummer", "Ort",
        "Standort", "LKW Anzahl", "Prio", "Paletten Eingang"
    ) + palHeaders + listOf(
        "SOLL", "IST", "Offen", "Abgeschlossen"
    )

    val zustandIdx = headersTop.indexOf("Zustand")
    val cellColors = eintraege.associate {
        (waAuskunftKopfRowId(it) to zustandIdx) to CellColor(
            Color(
                ZustandManager.colorHexFor(
                    it.zustand
                )
            )
        )
    }

    TabularScreen(
        screenKey = "WAAuskunftScreen_Kopf",
        rollenKey = "WAAuskunftScreen",
        title = "Warenausgang - Auskunft",
        headers = headersTop,
        models = (searchedKopf ?: eintraege).map { it.copy(id = waAuskunftKopfRowId(it)) },
        data = (searchedKopf ?: eintraege).map {
            waAuskunftKopfRowId(it) to mapKontrolleKopfRow(
                it,
                lkwEintraege,
                palettenDefs
            )
        },
        cellBackgroundColors = cellColors,
        nonEditableCols = headersTop.toSet(),
        editable = false,
        includeMetadata = true,
        onRowClick = { id ->
            selectedTournummer = (searchedKopf ?: eintraege)
                .find { waAuskunftKopfRowId(it) == id }?.lieferscheinnummer
            searchedDetails = null
        },
        onSearchWithFilters = { filters ->
            scope.launch { searchedKopf = YardApi.searchWaAuskunft(filters, "kopf") }
        },
        onCommit = {}
    )

    val details = remember(selectedTournummer, lkwEintraege) {
        if (selectedTournummer.isNullOrBlank()) emptyList()
        else lkwEintraege.filter { it.lieferscheinnummer == selectedTournummer }
    }
    if (details.isNotEmpty()) WAKontrolleDetailsTable(
        searchedDetails ?: details, standorteByTour, kontrolleDefs,
        onSearch = { filters ->
            scope.launch {
                searchedDetails = YardApi.searchWaAuskunft(filters, "detail", selectedTournummer.orEmpty())
            }
        }
    )
}

private fun mapKontrolleKopfRow(entry: All, lkwEintraege: List<All>, palettenDefs: List<All>): List<String> {
    val matchingLkw = lkwEintraege.filter { it.lieferscheinnummer == entry.lieferscheinnummer }
    val palettenSummen = palettenDefs.associate { def ->
        def.bezeichnung to (entry.palettenWerte[def.bezeichnung]?.toIntOrNull() ?: 0)
    }
    val palValues = palettenDefs.map { def -> palettenSummen[def.bezeichnung]?.takeIf { it > 0 }?.toString().orEmpty() }
    val soll = palettenSummen.values.sum()
    val ist = matchingLkw.sumOf { waIstPalettenanzahl(it) }
    val leerpaletten = matchingLkw.sumOf { it.leerpalettenanzahl }
    val offen = (soll - ist).coerceAtLeast(0)
    val lkwCount = matchingLkw.size

    return listOf(
        entry.lfdNr.toString(),
        entry.datum,
        entry.uhrzeitAnmeldung,
        "${entry.zustand} - ${ZustandManager.textFor(entry.zustand)}",
        entry.lieferscheinnummer,
        entry.ort,
        entry.standort,
        lkwCount.toString(),
        entry.prio,
        leerpaletten.toString()
    ) + palValues + listOf(
        soll.toString(),
        ist.takeIf { it > 0 }?.toString().orEmpty(),
        offen.toString(),
        entry.uhrzeitAbgeschlossen
    )
}

private fun waAuskunftKopfRowId(entry: All): String =
    listOf(
        entry.lieferscheinnummer,
        entry.id,
        entry.lfdNr.takeIf { it > 0 }?.toString().orEmpty()
    )
        .filter { it.isNotBlank() }
        .joinToString("|")
        .ifBlank { "wa-kopf-${entry.hashCode()}" }

private fun waAuskunftLkwRowId(entry: All): String =
    listOf(
        entry.lieferscheinnummer,
        entry.id,
        entry.lfdNr.takeIf { it > 0 }?.toString().orEmpty(),
        entry.lkwkennzeichen,
        entry.aufliegerkennzeichen
    )
        .filter { it.isNotBlank() }
        .joinToString("|")
        .ifBlank { "wa-lkw-${entry.hashCode()}" }

private val baseHeaders = listOf(
    "PIN", "PIN bestätigt", "lieferscheinnummer", "Zustand", "Unternehmer", "Auftraggeber",
    "Fahrer", "LKW", "Auflieger", "Standort", "WA Platz", "Tor", "Anmeldung",
    "Torvergabe", "Abschluss", "Paletten Ausgang", "Paletten Eingang", "Vorgeladen"
)

@Composable
private fun WAKontrolleDetailsTable(
    lkw: List<All>,
    standorteByTour: Map<String, List<All>>,
    defs: List<All>,
    onSearch: (Map<String, String>) -> Unit
) {
    val headers = baseHeaders + defs.map { it.bezeichnung }
    val zustandIdx = baseHeaders.indexOf("Zustand")
    val cellColors = lkw.associate {
        (waAuskunftLkwRowId(it) to zustandIdx) to CellColor(
            Color(
                ZustandManager.colorHexFor(
                    it.zustand
                )
            )
        )
    }

    TabularScreen(
        screenKey = "WAAuskunftScreen_LKW",
        rollenKey = "WAAuskunftScreen",
        title = "LKW-Details - Auskunft",
        headers = headers,
        models = lkw.map { it.copy(id = waAuskunftLkwRowId(it)) },
        data = lkw.map { waAuskunftLkwRowId(it) to mapKontrolleLkwRow(it, standorteByTour, defs) },
        cellBackgroundColors = cellColors,
        toggleColumns = setOf("PIN bestätigt"),
        nonEditableCols = headers.toSet(),
        editable = false,
        includeMetadata = true,
        onSearchWithFilters = onSearch,
        onCommit = {}
    )
}

private fun mapKontrolleLkwRow(
    entry: All,
    standorteByTour: Map<String, List<All>>,
    defs: List<All>
): List<String> {
    val waStandort = waStandortForLkw(entry, standorteByTour)
    val base = listOf(
        yardPinCode(entry),
        yardPinBestaetigtLabel(entry),
        entry.lieferscheinnummer,
        "${entry.zustand} - ${ZustandManager.textFor(entry.zustand)}",
        entry.unternehmer,
        entry.auftraggeber,
        entry.fahrername,
        entry.lkwkennzeichen,
        entry.aufliegerkennzeichen,
        entry.standort,
        waStandort?.platzcode.orEmpty().ifBlank { entry.platzcode.ifBlank { entry.platz } },
        waStandort?.tor?.toString().orEmpty().ifBlank { entry.tor?.toString().orEmpty() },
        entry.uhrzeitAnmeldung,
        entry.uhrzeitTorvergabe,
        entry.uhrzeitAbgeschlossen,
        waIstPalettenanzahl(entry).takeIf { it > 0 }?.toString().orEmpty(),
        entry.leerpalettenanzahl.toString(),
        waVorgeladenPaletten(entry).toString()
    )
    val kontrolleValues = defs.map { def -> waKontrollValue(entry, def) }
    return base + kontrolleValues
}

private fun waKontrollValue(entry: All, def: All): String =
    normalizeWaKontrollAnzeige(
        entry.palettenWerte["mobil.kontrolle.${def.id.ifBlank { def.kontrollId }}"].orEmpty()
        .ifBlank { entry.palettenWerte[def.bezeichnung].orEmpty() }
        .ifBlank { entry.palettenWerte[def.kontrollBezeichnung].orEmpty() },
        def
    )

private fun normalizeWaKontrollAnzeige(value: String, def: All): String {
    if (!def.kontrollTyp.equals("checkbox", true)) return value
    return when (value.trim().lowercase()) {
        "\u2705", "ja", "true", "1" -> "\u2705"
        "\u2B1C", "nein", "false", "0" -> "\u2B1C"
        else -> value
    }
}

private fun waIstPalettenanzahl(entry: All): Int =
    entry.yardabschlussIstPaletten.takeIf { it > 0 }
        ?: entry.mobilIstPaletten.takeIf { it > 0 }
        ?: entry.palettenanzahl.takeIf { entry.zustand >= ZustandManager.ABGESCHLOSSEN && it > 0 }
        ?: 0

private fun waVorgeladenPaletten(entry: All): Int =
    entry.driverVorgeladenPaletten

private fun waStandortForLkw(entry: All, standorteByTour: Map<String, List<All>>): All? {
    val standorte = standorteByTour[entry.lieferscheinnummer].orEmpty()
    if (standorte.isEmpty()) return null
    return standorte.firstOrNull { standort ->
        standort.id == entry.standort ||
            standort.standort == entry.standort ||
            standort.ort == entry.ort ||
            (entry.platzcode.isNotBlank() && standort.platzcode == entry.platzcode)
    } ?: standorte.firstOrNull()
}

